import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { v1Get, v1Post, v1Patch } from '@/lib/api';
import { UNAUTHORIZED_EDIT_MSG, toastMutationError } from '@/lib/resumeEnrichmentErrors';
import { useHasPermission, useMyPermissions } from '@/auth/permissions';

export function useResumeEnrichmentWorkspace() {
  const { candidateId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isLoading: permsLoading } = useMyPermissions();
  // Profile header fields → candidate.edit; approve API → bulk_enrichment.
  // Section UI requires both so users without Edit candidate profile cannot Add / Refine.
  const canEditProfile = useHasPermission('candidate.edit');
  const canEnrich = useHasPermission('candidate.bulk_enrichment');
  // While permissions are loading, treat as not-yet-known (do not toast; wait).
  const profileEditAllowed = !permsLoading && canEditProfile;
  const enrichAllowed = !permsLoading && canEnrich;
  const sectionEditAllowed = profileEditAllowed && enrichAllowed;
  // Banner only after permissions resolve — avoid flash of "denied" while loading.
  const profileEditDenied = !permsLoading && !canEditProfile;
  const enrichEditDenied = !permsLoading && !canEnrich;

  const evaluationId = searchParams.get('evaluation_id') || searchParams.get('eval') || null;
  const evalQs = evaluationId
    ? `?evaluation_id=${encodeURIComponent(evaluationId)}`
    : '';

  // --- Fetch candidate (for header) + company resume (for editor) -----------
  const candidateQ = useQuery({
    queryKey: ['v1-candidate', candidateId, evaluationId],
    queryFn: () => v1Get(`/candidates/${candidateId}${evalQs}`),
    enabled: !!candidateId && !!evaluationId,
  });
  const resumeQ = useQuery({
    queryKey: ['v1-candidate-company-resume', candidateId, evaluationId],
    queryFn: () => v1Get(`/candidates/${candidateId}/company-resume${evalQs}`),
    enabled: !!candidateId && !!evaluationId,
    retry: (count, err) => (err?.response?.status === 404 ? false : count < 2),
  });

  const c = candidateQ.data;
  const r = resumeQ.data;

  // --- Auto-save section edit ---------------------------------------------
  // Sprint 11.5 cleanup (Bug 3): the same company-resume is queried under two
  // different keys across the app (`v1-company-resume` in CandidateDetail.jsx
  // and `v1-candidate-company-resume` here). After every mutation we update
  // OUR cache via setQueryData (instant local refresh) AND invalidate the
  // sibling CandidateDetail key so that navigating back doesn't show stale
  // content.
  const refreshAllResumeCaches = (saved) => {
    qc.setQueryData(['v1-candidate-company-resume', candidateId, evaluationId], saved);
    qc.invalidateQueries({ queryKey: ['v1-company-resume', candidateId] });
    qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
  };

  const editMut = useMutation({
    mutationFn: ({ section_key, value }) =>
      v1Post(
        `/candidates/${candidateId}/company-resume/section-edit${evalQs}`,
        { section_key, value },
      ),
    onSuccess: (saved) => {
      refreshAllResumeCaches(saved);
    },
    onError: (e) => toastMutationError(e, 'Auto-save failed'),
  });

  // --- Approve & finish ---------------------------------------------------
  const approveMut = useMutation({
    mutationFn: () => v1Post(`/candidates/${candidateId}/company-resume/approve${evalQs}`, {}),
    onSuccess: (saved) => {
      refreshAllResumeCaches(saved);
      // Sprint 11.5 cleanup (Bug 2): the enrichment queue list uses the
      // ['v1-enrichment-queue', qs] key, NOT 'v1-resume-enrichment-queue'.
      // Invalidating the matching prefix is what actually refreshes the list.
      qc.invalidateQueries({ queryKey: ['v1-enrichment-queue'] });
      toast.success('Resume approved. Returning to queue.');
      navigate('/portal/resume-enrichment', { replace: true });
    },
    onError: (e) => toastMutationError(e, 'Approval failed'),
  });

  // --- Profile patch (Sprint 11.5: Edit Profile lives here now) -----------
  const profilePatchMut = useMutation({
    mutationFn: (patch) => v1Patch(`/candidates/${candidateId}`, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['v1-candidate', candidateId, evaluationId], updated);
      qc.invalidateQueries({ queryKey: ['v1-candidates'] });
      qc.invalidateQueries({ queryKey: ['v1-company-resume', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-candidate-company-resume', candidateId] });
    },
    onError: (e) => toastMutationError(e, 'Save failed'),
  });

  const saveProfile = (patch) => {
    if (permsLoading) return;
    if (!canEditProfile) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    profilePatchMut.mutate(patch);
  };

  const saveSection = (section_key, value) => {
    if (permsLoading) return;
    if (!canEditProfile || !canEnrich) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    editMut.mutate({ section_key, value });
  };

  const approveResume = () => {
    if (permsLoading) return;
    if (!canEnrich) {
      toast.warning(UNAUTHORIZED_EDIT_MSG);
      return;
    }
    approveMut.mutate();
  };

  return {
    candidateId,
    evaluationId,
    candidateQ,
    resumeQ,
    c,
    r,
    profileEditAllowed,
    enrichAllowed,
    sectionEditAllowed,
    profileEditDenied,
    enrichEditDenied,
    profilePatchMut,
    approveMut,
    saveProfile,
    saveSection,
    approveResume,
  };
}
